import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
    Autocomplete,
    Button,
    Checkbox,
    Dialog,
    FormControlLabel,
    FormHelperText,
    Stack,
    TextField
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers-pro';
import { useMuiController, useMuiForm } from '@zenput/util-common';
import { useSnackbar } from 'notistack';
import { FieldError } from 'react-hook-form';
import { makeStyles } from 'tss-react/mui';
import { z, ZodIssueCode } from 'zod';
import { MobileAccounts } from '../../../backbone-unorganized/models';
import AppBar from '../../shared/AppBar';
import BottomBar from '../../shared/BottomBar';
import PageLayout from '../../shared/PageLayout';
import { DeleteFollowUpActionModal } from './DeleteFollowUpActionModal';
import { FollowUpActionType } from './FollowUpActions';
import { FollowUpActionsGallery } from './FollowUpActionsGallery';

const useStyles = makeStyles()((theme) => {
    const { spacing } = theme;

    return {
        bottomBar: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            padding: spacing(2),
            textTransform: 'none',
            backgroundColor: 'white'
        },
        bottomButton: {
            textTransform: 'none',
            padding: `${spacing(1.5)} 0`
        },
        formContainer: {
            backgroundColor: 'white',
            marginTop: spacing(2),
            marginBottom: spacing(2),
            padding: spacing(2)
        },
        formInput: {
            marginTop: spacing(2),
            '& .MuiInputBase-input': {
                border: 'none',
                padding: `${spacing(2)} 0`,
                borderBottom: 'groove'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                paddingLeft: spacing(1.5),
                paddingTop: spacing(4.5),
                color: 'black !important'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        },
        textFieldInput: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                border: 'none',
                background: 'none'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                fontSize: '14px',
                color: 'black !important'
            }
        },
        dateFieldInput: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                padding: `${spacing(2.5)} 0`,
                border: 'none',
                color: 'black !important'
            },
            '& .MuiInputBase-input:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
                borderBottom: 'groove'
            },
            '& .MuiOutlinedInput-notchedOutline:focus': {
                borderColor: 'currentcolor !important'
            },
            '& .MuiFilledInput-input': {
                paddingLeft: spacing(1.5),
                paddingTop: spacing(4.5),
                color: 'black !important'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        },
        autoSelect: {
            marginTop: spacing(2),
            borderBottom: 'groove',
            '& .MuiInputBase-input': {
                minHeight: '36px',
                border: 'none',
                background: 'none',
                padding: `${spacing(2)} 0`,
                color: 'black !important'
            },
            '& .MuiFormControl-root': {
                height: 'auto'
            },
            '& .MuiInputLabel-filled': {
                paddingTop: spacing(0.25)
            }
        }
    };
});

const getTomorrow = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    return tomorrow;
};
const TITLE_MAX_CHARACTERS = 255;
const DESCRIPTION_MAX_CHARACTERS = 5000;
export type FollowUpActionMode = 'add' | 'edit';

export const FollowUpActionSchema = z.object({
    title: z.string().min(1).max(TITLE_MAX_CHARACTERS),
    description: z.string().min(1).max(DESCRIPTION_MAX_CHARACTERS),
    requirePhoto: z.boolean(),
    dueDate: z.date(),
    location: z.object({ id: z.number(), label: z.string() }),
    images: z.array(z.string())
});

const emptyFollowUpAction = {
    title: '',
    description: '',
    requirePhoto: false,
    dueDate: null,
    location: null,
    images: []
};

interface FollowUpActionsFormProps {
    open: boolean;
    onGoBack: () => void;
    mode: FollowUpActionMode;
    initialFollowUpAction: FollowUpActionType;
    handleFollowUpAction: (fua: FollowUpActionType) => void;
}

export const FollowUpActionForm = (props: FollowUpActionsFormProps) => {
    const { open, onGoBack, mode, initialFollowUpAction, handleFollowUpAction } = props;
    const { classes } = useStyles();
    const { enqueueSnackbar } = useSnackbar();

    const [accounts, setAccounts] = useState<{ id: number; label: string }[]>([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const isEditMode = mode === 'edit';

    const {
        control,
        handleSubmit,
        reset,
        formState: { errors }
    } = useMuiForm<z.infer<typeof FollowUpActionSchema>>({
        resolver: zodResolver(FollowUpActionSchema),
        defaultValues: emptyFollowUpAction
    });

    const getError = (error?: FieldError) => {
        const zodErrorCodeMap: Record<string, string | undefined> = {
            invalid_type: _t('mobile_mobileform_add_follow_up_actions_forms_required'),
            too_small: _t('mobile_mobileform_add_follow_up_actions_forms_required'),
            too_big: _t('mobile_mobileform_add_follow_up_actions_forms_too_many_characters')
        } satisfies Partial<Record<ZodIssueCode, string>>;

        return error
            ? {
                  error: true,
                  helperText: zodErrorCodeMap[error.type] ?? _t('mobile_mobileform_add_follow_up_actions_forms_generic')
              }
            : {};
    };

    const { field: titleField } = useMuiController({ control, name: 'title' });
    const { field: descriptionField } = useMuiController({ control, name: 'description' });
    const { field: requirePhotoField } = useMuiController({ control, name: 'requirePhoto' });
    const { field: dueDateField } = useMuiController({ control, name: 'dueDate' });
    const { field: locationField } = useMuiController({ control, name: 'location' });
    const { field: imagesField } = useMuiController({ control, name: 'images' });

    useEffect(() => {
        const mobileAccounts = new MobileAccounts();
        mobileAccounts.fetch({
            success: function () {
                const locationData = mobileAccounts.models.map((model) => ({
                    id: model.get('id'),
                    label: model.get('name') || ''
                }));
                setAccounts(locationData);
            },
            error: function () {
                enqueueSnackbar(JSON.stringify(_t('mobile_mobileform_add_follow_up_actions_error_locations')), {
                    variant: 'error'
                });
            }
        });
    }, []);

    useEffect(() => {
        if (mode == 'edit') {
            reset({
                title: initialFollowUpAction.title,
                description: initialFollowUpAction.description,
                requirePhoto: initialFollowUpAction.requirePhoto || false,
                dueDate: initialFollowUpAction.dueDate,
                location: initialFollowUpAction.location,
                images: initialFollowUpAction.images || []
            });
        } else {
            reset(emptyFollowUpAction);
        }
    }, [initialFollowUpAction, open, mode]);

    const handleDiscardOrDelete = () => {
        if (isEditMode) {
            setShowDeleteModal(true);
        } else {
            onGoBack();
        }
    };

    const handleDeleteFollowUpAction = () => {
        const tempData = { ...initialFollowUpAction, delete: true };
        handleFollowUpAction(tempData);
        setShowDeleteModal(false);
    };

    const onSubmit = handleSubmit((data) => {
        let tempData: FollowUpActionType = data;
        if (isEditMode) {
            tempData = { ...data, id: initialFollowUpAction.id };
        }
        handleFollowUpAction(tempData);
    });

    return (
        <>
            <Dialog fullScreen open={open} onClose={onGoBack}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <PageLayout>
                        {{
                            appBar: (
                                <AppBar
                                    title={_t('mobile_mobileform_add_follow_up_actions_add_follow_up_title')}
                                    onGoBack={onGoBack}
                                />
                            ),
                            content: (
                                <div className={classes.formContainer}>
                                    <Stack component="form" onSubmit={onSubmit}>
                                        <FollowUpActionsGallery {...imagesField} />

                                        <TextField
                                            className={classes.formInput}
                                            label={_t('mobile_mobileform_add_follow_up_actions_forms_title')}
                                            variant="filled"
                                            required
                                            {...titleField}
                                            {...getError(errors.title)}
                                        />
                                        <TextField
                                            className={classes.textFieldInput}
                                            label={_t('mobile_mobileform_add_follow_up_actions_forms_description')}
                                            variant="filled"
                                            required
                                            multiline
                                            data-testid="controlled-input2"
                                            error={getError(errors.description).error}
                                            {...descriptionField}
                                        />
                                        {errors.description && (
                                            <FormHelperText sx={{ marginLeft: 2 }} error>
                                                {getError(errors.description).helperText}
                                            </FormHelperText>
                                        )}

                                        <FormControlLabel
                                            sx={{ padding: 1 }}
                                            control={
                                                <Checkbox
                                                    sx={{ marginLeft: 1 }}
                                                    checked={requirePhotoField.value}
                                                    {...requirePhotoField}
                                                />
                                            }
                                            label={_t('mobile_mobileform_add_follow_up_actions_forms_photo')}
                                        />
                                        <MobileDateTimePicker
                                            className={classes.dateFieldInput}
                                            label={_t('mobile_mobileform_add_follow_up_actions_forms_due_date')}
                                            value={dueDateField.value ?? null}
                                            closeOnSelect
                                            minDateTime={getTomorrow()}
                                            slotProps={{
                                                textField: {
                                                    variant: 'filled',
                                                    slotProps: {
                                                        input: { endAdornment: <CalendarTodayIcon /> }
                                                    },
                                                    required: true,
                                                    error: getError(errors.dueDate).error
                                                }
                                            }}
                                            {...dueDateField}
                                        />
                                        {errors.dueDate && (
                                            <FormHelperText sx={{ marginLeft: 2 }} error>
                                                {getError(errors.dueDate).helperText}
                                            </FormHelperText>
                                        )}

                                        <Autocomplete
                                            className={classes.autoSelect}
                                            disablePortal
                                            options={accounts}
                                            getOptionKey={(option) => option.id}
                                            onChange={(_event, newValue) => {
                                                locationField.onChange(newValue);
                                            }}
                                            value={locationField.value || null}
                                            renderInput={(params) => (
                                                <TextField
                                                    variant="filled"
                                                    label={_t(
                                                        'mobile_mobileform_add_follow_up_actions_forms_locations'
                                                    )}
                                                    sx={{ height: 60 }}
                                                    required
                                                    error={getError(errors.location as FieldError).error}
                                                    {...params}
                                                />
                                            )}
                                        />
                                        {'location' in errors && (
                                            <FormHelperText sx={{ marginLeft: 2 }} error>
                                                {getError(errors.location as FieldError).helperText}
                                            </FormHelperText>
                                        )}
                                    </Stack>
                                </div>
                            ),
                            bottomBar: (
                                <BottomBar>
                                    <div className={classes.bottomBar}>
                                        <Button
                                            className={classes.bottomButton}
                                            variant="contained"
                                            color="primary"
                                            onClick={onSubmit}
                                        >
                                            {isEditMode
                                                ? _t('mobile_mobileform_add_follow_up_actions_forms_update_button')
                                                : _t('mobile_mobileform_add_follow_up_actions_forms_add_button')}
                                        </Button>
                                        <Button
                                            className={classes.bottomButton}
                                            color={isEditMode ? 'error' : 'info'}
                                            onClick={handleDiscardOrDelete}
                                        >
                                            {isEditMode
                                                ? _t('mobile_mobileform_add_follow_up_actions_forms_delete_button')
                                                : _t('mobile_mobileform_add_follow_up_actions_forms_discard')}
                                        </Button>
                                    </div>
                                </BottomBar>
                            )
                        }}
                    </PageLayout>
                </LocalizationProvider>
            </Dialog>
            <DeleteFollowUpActionModal
                onSubmit={handleDeleteFollowUpAction}
                showConfirmation={showDeleteModal}
                handleCloseConfirmation={() => setShowDeleteModal(false)}
            />
        </>
    );
};
